import { API_BASE } from "../utils/api";
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/Toast";

export default function Profile() {
  const { user, token, logout } = useAuth();
  const { addToast } = useToast();

  const [formData, setFormData] = useState({
    full_name: "",
    company_name: "",
    phone: "",
    tax_number: "",
    address: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Password change state
  const [pwData, setPwData] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState("");

  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || "",
        company_name: user.company_name || "",
        phone: user.phone || "",
        tax_number: user.tax_number || "",
        address: user.address || "",
      });
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/auth/profile`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to save profile");
      addToast("Profile updated successfully!", "success");
    } catch (err) {
      addToast(err.message, "error");
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const getInitials = () => {
    const name = user?.full_name || user?.email || "U";
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const inputClass = "w-full border border-luxury-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-luxury-gold/40 focus:border-luxury-gold/60 bg-luxury-elevated text-luxury-text placeholder-luxury-muted transition";

  return (
    <div className="min-h-screen p-6 lg:p-8 bg-luxury-bg">
      <div className="max-w-2xl mx-auto">

        {/* Page header */}
        <div className="mb-7">
          <h1 className="text-2xl font-bold text-luxury-text">My Profile</h1>
          <p className="text-sm mt-1 text-luxury-secondary">Manage your account and personal information</p>
        </div>

        {/* Avatar Card */}
        <div className="rounded-2xl border border-luxury-border bg-luxury-surface p-6 mb-5 flex items-center gap-5">
          <div className="w-20 h-20 bg-luxury-elevated border border-luxury-gold/40 rounded-2xl flex items-center justify-center flex-shrink-0">
            <span className="text-luxury-gold font-bold text-2xl">{getInitials()}</span>
          </div>
          <div className="min-w-0">
            <h2 className="text-xl font-bold truncate text-luxury-text">{user?.full_name || "—"}</h2>
            <p className="text-sm text-luxury-secondary">{user?.email}</p>
            <span className="inline-block mt-2 px-3 py-1 text-xs font-semibold rounded-full bg-luxury-elevated border border-luxury-border text-luxury-secondary">
              {user?.company_name || "No company set"}
            </span>
          </div>
        </div>

        {/* Profile Form */}
        <form onSubmit={handleSave} className="rounded-2xl border border-luxury-border bg-luxury-surface p-8 mb-5">
          <h3 className="text-base font-bold mb-6 text-luxury-text">Account Details</h3>

          <div className="space-y-5">
            <div>
              <label className="block text-sm font-semibold mb-1.5 text-luxury-secondary">Full Name</label>
              <input name="full_name" type="text" value={formData.full_name}
                onChange={handleChange} placeholder="Your full name" className={inputClass} />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1.5 text-luxury-secondary">Email Address</label>
              <input type="email" value={user?.email || ""} disabled
                className="w-full border border-luxury-border rounded-xl px-4 py-3 text-sm bg-luxury-bg text-luxury-muted cursor-not-allowed" />
              <p className="text-xs mt-1 text-luxury-muted">Email cannot be changed.</p>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1.5 text-luxury-secondary">Company Name</label>
              <input name="company_name" type="text" value={formData.company_name}
                onChange={handleChange} placeholder="Your company or business name" className={inputClass} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-1.5 text-luxury-secondary">Phone Number</label>
                <input name="phone" type="tel" value={formData.phone}
                  onChange={handleChange} placeholder="+27 xx xxx xxxx" className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5 text-luxury-secondary">SA Tax Number</label>
                <input name="tax_number" type="text" value={formData.tax_number}
                  onChange={handleChange} placeholder="SARS tax number" className={inputClass} />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1.5 text-luxury-secondary">Business Address</label>
              <textarea name="address" value={formData.address} onChange={handleChange}
                placeholder="Your business address" rows={3}
                className={`${inputClass} resize-none`} />
            </div>
          </div>

          {error && (
            <div className="mt-4 p-3 border border-red-800/50 rounded-xl text-sm flex items-center gap-2 bg-red-900/20 text-red-400">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          )}

          <button type="submit" disabled={saving}
            className={`mt-6 w-full py-3 rounded-xl font-semibold text-white text-sm transition-all duration-200
              ${saving ? "bg-luxury-elevated text-luxury-muted cursor-not-allowed border border-luxury-border" : "bg-luxury-cta hover:bg-luxury-cta-hover active:scale-95"}`}>
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </form>

        {/* Change Password */}
        <div className="rounded-2xl border border-luxury-border bg-luxury-surface p-8 mb-5">
          <h3 className="text-base font-bold mb-6 text-luxury-text">Change Password</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-1.5 text-luxury-secondary">Current Password</label>
              <input type="password" value={pwData.current_password}
                onChange={(e) => setPwData({ ...pwData, current_password: e.target.value })}
                placeholder="Enter current password" className={inputClass} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-1.5 text-luxury-secondary">New Password</label>
                <input type="password" value={pwData.new_password}
                  onChange={(e) => setPwData({ ...pwData, new_password: e.target.value })}
                  placeholder="Min 8 characters" className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5 text-luxury-secondary">Confirm New</label>
                <input type="password" value={pwData.confirm_password}
                  onChange={(e) => setPwData({ ...pwData, confirm_password: e.target.value })}
                  placeholder="Repeat new password" className={inputClass} />
              </div>
            </div>
            {pwError && (
              <div className="p-3 border border-red-800/50 rounded-xl text-sm bg-red-900/20 text-red-400">{pwError}</div>
            )}
            <button
              onClick={async () => {
                setPwError("");
                if (pwData.new_password !== pwData.confirm_password) { setPwError("Passwords do not match."); return; }
                if (pwData.new_password.length < 8) { setPwError("New password must be at least 8 characters."); return; }
                setPwSaving(true);
                try {
                  const res = await fetch(`${API_BASE}/api/auth/change-password`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ current_password: pwData.current_password, new_password: pwData.new_password }),
                  });
                  const data = await res.json();
                  if (!res.ok) throw new Error(data.detail || "Failed to change password");
                  addToast("Password changed successfully!", "success");
                  setPwData({ current_password: "", new_password: "", confirm_password: "" });
                } catch (err) { setPwError(err.message); addToast(err.message, "error"); }
                finally { setPwSaving(false); }
              }}
              disabled={pwSaving}
              className={`w-full py-3 rounded-xl font-semibold text-sm transition-all duration-200
                ${pwSaving
                  ? "bg-luxury-elevated text-luxury-muted cursor-not-allowed border border-luxury-border"
                  : "bg-luxury-elevated border border-luxury-border text-luxury-secondary hover:bg-luxury-bg hover:text-luxury-text active:scale-95"}`}
            >
              {pwSaving ? "Updating..." : "Update Password"}
            </button>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="rounded-2xl border border-red-900/40 bg-luxury-surface p-6">
          <h3 className="text-base font-bold mb-1 text-red-400">Danger Zone</h3>
          <p className="text-sm mb-4 text-luxury-secondary">
            Logging out will clear your session. All your data remains safe in the cloud.
          </p>
          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-5 py-2 border border-red-800/60 rounded-lg text-sm font-semibold transition text-red-400 hover:bg-red-900/20"
            >
              Logout from all devices
            </button>
          ) : (
            <div className="flex items-center gap-3">
              <p className="text-sm font-medium text-luxury-secondary">Are you sure?</p>
              <button onClick={logout}
                className="px-4 py-2 bg-red-700 text-white rounded-lg text-sm font-semibold hover:bg-red-600 transition">
                Yes, Logout
              </button>
              <button onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-luxury-border rounded-lg text-sm font-semibold transition text-luxury-secondary hover:bg-luxury-elevated">
                Cancel
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
